// client/src/services/adminService.js
//
// Client API wrapper for admin endpoints.
//
// CHANGES IN THIS REVISION (Shareable Links & Reactions build):
//   - Added Reaction Emoji Library methods (CRUD, reorder, deactivate/reactivate).
//   - Added Template Reactions Assignment methods.
//   - Existing methods unchanged.

import api from './api.js';

export const adminService = {
  // Dashboard
  getDashboard: () => api.get('/admin/dashboard'),

  // User Types / Tiers
  getUserTypes: () => api.get('/admin/user-types'),
  updateUserType: (id, data) => api.put(`/admin/user-types/${id}`, data),
  getPlanDeliveryTypes: async () => {
    return api.get('/admin/plan-delivery-types');
  },

  // Templates
  getTemplates: () => api.get('/admin/templates'),
  createTemplate: (data) => api.post('/admin/templates', data),
  updateTemplate: (id, data) => api.put(`/admin/templates/${id}`, data),
  deleteTemplate: (id) => api.del(`/admin/templates/${id}`),
  assignMusic: (templateId, musicIds, defaultMusicId) => api.post(`/admin/templates/${templateId}/music`, { musicIds, defaultMusicId }),

  // Template Parts (multi-file template support)
  // A template can be split into multiple smaller HTML parts that the
  // server concatenates at render time. See database/schema19.sql.
  getTemplateParts: (templateId) => api.get(`/admin/templates/${templateId}/parts`),
  createTemplatePart: (templateId, data) => api.post(`/admin/templates/${templateId}/parts`, data),
  updateTemplatePart: (templateId, partId, data) => api.put(`/admin/templates/${templateId}/parts/${partId}`, data),
  deleteTemplatePart: (templateId, partId) => api.del(`/admin/templates/${templateId}/parts/${partId}`),
  reorderTemplateParts: (templateId, orderedIds) => api.put(`/admin/templates/${templateId}/parts/reorder`, { orderedIds }),

  // ─── Reaction Emoji Library ──────────────────────────────
  //
  // Master list of clickable reaction emojis (❤️, 👍, 🎉, 🙏, etc.).
  // Each emoji has the unicode character itself stored in the DB —
  // no image hosting / R2 needed. Per-template assignments live in
  // template_reactions (see below).
  //
  // Server: see server/routes/admin.js — Reaction Emoji Library section.

  // List the full library with usage counts. Returns inactive entries
  // too, so admin can see retired emojis and reactivate if desired.
  getReactionEmojis: () => api.get('/admin/reaction-emojis'),

  // Add a new emoji.
  //   data: { emoji_char, display_name, description?, isactive?, isdefault?, display_order? }
  // emoji_char must be the actual unicode character. UNIQUE constraint
  // returns 409 if the emoji is already in the library.
  createReactionEmoji: (data) => api.post('/admin/reaction-emojis', data),

  // Edit an emoji's metadata (and optionally swap its character).
  // Same body shape as create.
  updateReactionEmoji: (id, data) => api.put(`/admin/reaction-emojis/${id}`, data),

  // Soft-disable an emoji.
  //   mode: 'keep'   → leave existing template assignments in place
  //   mode: 'remove' → wipe junction rows from every template
  // Historical card_reactions counts are preserved either way.
  deactivateReactionEmoji: (id, mode) =>
    api.patch(`/admin/reaction-emojis/${id}/deactivate`, { mode }),

  // Re-enable a previously deactivated emoji. Template assignments are
  // not restored automatically — admin must re-add it where wanted.
  reactivateReactionEmoji: (id) =>
    api.patch(`/admin/reaction-emojis/${id}/reactivate`, {}),

  // Hard delete. Blocked by server (400) if any historical card_reactions
  // reference this emoji. Use deactivate to preserve history.
  deleteReactionEmoji: (id) => api.del(`/admin/reaction-emojis/${id}`),

  // Reorder the library via drag handles.
  //   orderedIds: [emojiid, emojiid, ...] — renumbered to display_order 1, 2, 3...
  reorderReactionEmojis: (orderedIds) =>
    api.put('/admin/reaction-emojis/reorder', { orderedIds }),

  // ─── Template Reactions Assignment ───────────────────────
  //
  // Per-template config: which emojis appear as clickable reactions
  // on cards using this template, in what order, and whether reactions
  // are enabled at all for the template.
  //
  // Cards rendered from a template with reactions_enabled = false show
  // no reactions regardless of what's in the junction. Cards from a
  // template with reactions_enabled = true but no junction rows fall
  // back to the default emoji set (reaction_emojis.isdefault = true).

  // Get the template's current config + full library in one call, so
  // the assignment modal can render the full picker and pre-check
  // already-assigned emojis without a second round-trip.
  // Returns:
  //   { reactions_enabled, assigned: [...], library: [...] }
  getTemplateReactions: (templateId) =>
    api.get(`/admin/templates/${templateId}/reactions`),

  // Replace the template's config atomically.
  //   data: {
  //     reactions_enabled: boolean,
  //     emojiids: [number, number, ...]  // ordered, can be empty
  //   }
  // Empty emojiids with reactions_enabled=true → card falls back to
  // default emoji set. reactions_enabled=false → no reactions on card.
  setTemplateReactions: (templateId, data) =>
    api.put(`/admin/templates/${templateId}/reactions`, data),

  // Music
  getMusic: () => api.get('/admin/music'),
  createMusic: (data) => api.post('/admin/music', data),
  updateMusic: (id, data) => api.put(`/admin/music/${id}`, data),

  // Moonbeam Categories
  getMoonbeamCategories: () => api.get('/admin/categories/moonbeam'),
  createMoonbeamCategory: (data) => api.post('/admin/categories/moonbeam', data),
  updateMoonbeamCategory: (id, data) => api.put(`/admin/categories/moonbeam/${id}`, data),
  deleteMoonbeamCategory: (id) => api.del(`/admin/categories/moonbeam/${id}`),

  // Story Categories
  getStoryCategories: () => api.get('/admin/categories/story'),

  // Industry Types
  getIndustryTypes: () => api.get('/admin/industry-types'),
  createIndustryType: (data) => api.post('/admin/industry-types', data),
  updateIndustryType: (id, data) => api.put(`/admin/industry-types/${id}`, data),
  deleteIndustryType: (id) => api.del(`/admin/industry-types/${id}`),

  // Contact Types (default types only)
  getContactTypes: () => api.get('/admin/contact-types'),
  createContactType: (data) => api.post('/admin/contact-types', data),
  updateContactType: (id, data) => api.put(`/admin/contact-types/${id}`, data),
  deleteContactType: (id) => api.del(`/admin/contact-types/${id}`),

  // Stories
  getStories: () => api.get('/admin/stories'),
  createStory: (data) => api.post('/admin/stories', data),
  updateStory: (id, data) => api.put(`/admin/stories/${id}`, data),
  deleteStory: (id) => api.del(`/admin/stories/${id}`),

  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (settings) => api.put('/admin/settings', { settings }),

  // Links
  getLinks: () => api.get('/admin/links'),
  createLink: (data) => api.post('/admin/links', data),
  updateLink: (id, data) => api.put(`/admin/links/${id}`, data),

  // Users
  getUsers: (params = '') => api.get(`/admin/users?${params}`),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.post(`/admin/users/${id}/delete`),

  // Analytics - summary stats (now accepts days parameter)
  getAnalytics: (days = 30) => api.get(`/admin/analytics?days=${days}`),

  // Analytics - detailed events with pagination and filtering
  getAnalyticsEvents: (params = '') => api.get(`/admin/analytics/events?${params}`),

  // Analytics - per-card performance (views + CTA clicks)
  getAnalyticsCards: (params = '') => api.get(`/admin/analytics/cards?${params}`),

  // Analytics - CTA conversions (users who received card, clicked CTA, then signed up)
  getCtaConversions: (params = '') => api.get(`/admin/analytics/cta-conversions?${params}`),

  // Analytics - User growth over time (for growth chart)
  getUserGrowth: (days = 30) => api.get(`/admin/analytics/user-growth?days=${days}`),

  // Analytics - Top senders leaderboard
  getTopSenders: (days = 30, limit = 10) => api.get(`/admin/analytics/top-senders?days=${days}&limit=${limit}`),

  // Analytics - CTA funnel data (views -> clicks -> conversions)
  getCtaFunnel: (days = 30) => api.get(`/admin/analytics/cta-funnel?days=${days}`),

  // Get all users for filter dropdown (lightweight list)
  getUsersList: () => api.get('/admin/users?limit=1000'),

  // Sending Domains (for multi-domain email reputation isolation)
  getSendingDomains: () => api.get('/admin/sending-domains'),
  getSendingDomain: (id) => api.get(`/admin/sending-domains/${id}`),
  createSendingDomain: (data) => api.post('/admin/sending-domains', data),
  updateSendingDomain: (id, data) => api.put(`/admin/sending-domains/${id}`, data),
  deleteSendingDomain: (id) => api.del(`/admin/sending-domains/${id}`),
  resetSendingDomainDailyCount: (id) => api.patch(`/admin/sending-domains/${id}/reset-daily-count`),
  getActiveSendingDomains: () => api.get('/admin/sending-domains-active'),

  // Reply Emails (for marketing campaign reply-to addresses)
  getReplyEmails: () => api.get('/admin/reply-emails'),
  getReplyEmail: (id) => api.get(`/admin/reply-emails/${id}`),
  createReplyEmail: (data) => api.post('/admin/reply-emails', data),
  updateReplyEmail: (id, data) => api.put(`/admin/reply-emails/${id}`, data),
  deleteReplyEmail: (id) => api.del(`/admin/reply-emails/${id}`),
  setDefaultReplyEmail: (id) => api.patch(`/admin/reply-emails/${id}/set-default`),
  getActiveReplyEmails: () => api.get('/admin/reply-emails-active'),
};

export default adminService;